import React, { useState, useRef, useEffect } from "react";
import { FaArrowLeft, FaBars } from "react-icons/fa";
import "../assets/css/TopHeader.css";
import { resolveImageUrl, getApiUrl } from "../config/apiConfig";
import { clearAuthSessionStorage } from "../utils/clearSessionStorage";

function TopHeader({
  pageTitle,
  showBackButton,
  onBackClick,
  onLogout,
  onMyDetails,
  onChangePassword,
  onPermissions,
  onToggleSidebar,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [user, setUser] = useState(null);
  const [imageError, setImageError] = useState(false);

  // Get user data from localStorage
  const getUserData = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (error) {
      console.error("[TopHeader] Error parsing user data:", error?.message || "Invalid user data.");
    }
    return null;
  };

  // Load user data and listen for changes
  useEffect(() => {
    const loadUserData = () => {
      setUser(getUserData());
    };
    
    // Load initially
    loadUserData();
    
    // Listen for storage changes (when localStorage is updated)
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === null) {
        loadUserData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event (for same-window updates)
    const handleUserUpdate = () => {
      loadUserData();
    };
    
    window.addEventListener('userDataUpdated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleUserUpdate);
    };
  }, []);

  const isAdminUser = (() => {
    if (!user) return false;
    const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
    const roleName = (
      (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
      user.role_name ||
      user.role ||
      ""
    ).toString().trim().toLowerCase();
    return roleName === "admin";
  })();
  const userName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || "User" : "User";
  const userEmail = user ? (user.email || user.company_email || "") : "";
  const userInitial = user && user.first_name ? user.first_name.charAt(0).toUpperCase() : "U";
  
  // Get and fix image URL (with cache-busting so updated image shows).
  // Backend must serve GET /uploads/cloud-office/users/... with 200 (public or same-origin) for the image to display.
  const getUserImageUrl = () => {
    if (!user) return null;

    let imageUrl = null;
    if (user.image_url) {
      imageUrl = resolveImageUrl(user.image_url);
    } else if (user.image_path) {
      const API_HOST = getApiUrl();
      if (API_HOST) {
        const base = API_HOST.replace(/\/$/, "");
        imageUrl = `${base}/uploads/cloud-office/users/${user.image_path}`;
      }
    }

    if (!imageUrl) return null;
    const separator = imageUrl.includes("?") ? "&" : "?";
    imageUrl = `${imageUrl}${separator}t=${user.updated_on || Date.now()}`;
    return imageUrl;
  };
  
  const userImageUrl = getUserImageUrl();
  
  // Reset image error when user or image URL changes
  useEffect(() => {
    setImageError(false);
  }, [user?.user_id, user?.image_url]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleUserInfoClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleLogout = () => {
    clearAuthSessionStorage();
    sessionStorage.clear();
    if (onLogout) {
      onLogout();
    }
    setShowDropdown(false);
  };

  const handleMyDetailsClick = () => {
    setShowDropdown(false);
    if (onMyDetails) {
      onMyDetails();
    }
  };

  const handleChangePasswordClick = () => {
    setShowDropdown(false);
    if (onChangePassword) {
      onChangePassword();
    }
  };

  const handlePermissionsClick = () => {
    setShowDropdown(false);
    if (onPermissions) {
      onPermissions();
    }
  };

  return (
    <div className="top-header">
      <div className="header-content">
        <div className="header-title-row">
          {onToggleSidebar && (
            <button
              type="button"
              className="sidebar-toggle"
              onClick={onToggleSidebar}
              aria-label="Toggle navigation menu"
            >
              <FaBars size={18} />
            </button>
          )}
          {showBackButton && onBackClick && (
            <button type="button" className="header-back-link" onClick={onBackClick} aria-label="Go back">
              <FaArrowLeft /> 
            </button>
          )}
          {pageTitle && <h1 className="page-title-header">{pageTitle}</h1>}
        </div>
        <div className="user-info-container" ref={dropdownRef}>
          <div className="user-info" onClick={handleUserInfoClick}>
            <div className="user-avatar">
              {userImageUrl && !imageError ? (
                <img 
                  src={userImageUrl} 
                  alt={userName}
                  onError={() => setImageError(true)}
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover"
                  }}
                />
              ) : (
                userInitial
              )}
            </div>
            <div className="user-details">
              <span className="user-name">{userName}</span>
              <div className="user-role">
                <span>{userEmail || "—"}</span>
                <span className={`dropdown-arrow ${showDropdown ? 'open' : ''}`}>▼</span>
              </div>
            </div>
          </div>
          
          {showDropdown && (
            <div className="user-dropdown">
              <div className="dropdown-item" onClick={handleMyDetailsClick}>
                My Details
              </div>
              <div className="dropdown-divider"></div>
              <div className="dropdown-item" onClick={handleChangePasswordClick}>
                Change Password
              </div>
              {isAdminUser && (
                <>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item" onClick={handlePermissionsClick}>
                    Permissions
                  </div>
                </>
              )}
              <div className="dropdown-divider"></div>
              <div className="dropdown-item" onClick={handleLogout}>
                Logout
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TopHeader;
